import React, { useEffect, useState } from 'react';
import { webLoadingFalse, webLoadingTrue, webNavigate } from '../../services/navigate.service';
import { getDetailTansactions } from '../../services/transaction.service';
import BackIcon from '../../images/BackIcon';
import { useResponsive } from '../../hook/useResponsive';
import {
  BORDER_SOFT,
  BORDER_STRONG,
  MONO,
  Pill,
  PillKind,
  TEAL,
  TEXT,
  TEXT_DIM,
  TEXT_FAINT,
  TEXT_MUTED,
  t,
} from '../../components/list';
import CardPaymentDialog from './CardPaymentDialog';
import {
  INK,
  MiniCard,
  SURFACE_SOFT,
  SalePayment,
  TENDER_TYPE_LABELS,
  TenderAvatar,
  cardBrandOf,
  endingIn,
  formatClock,
  formatDay,
  money,
  primaryAttempt,
  quantity,
  toDate,
  toNumber,
} from './recentSalesShared';

const TYPE_PILLS: Record<number, { label: string; kind: PillKind }> = {
  1: { label: 'Sale', kind: 'ok' },
  2: { label: 'Refund', kind: 'danger' },
  3: { label: 'Void', kind: 'off' },
};

// Hover and focus need real CSS (inline <style>: the host drops module-only classes); resting colours live here too.
const PAGE_CSS = `
.rs-detail-btn { background-color: #FFFFFF; }
.rs-detail-btn:not(:disabled):hover { background-color: #EBF9F9; }
.rs-detail-btn:focus-visible, .rs-detail-back:focus-visible { outline: 2px solid #0F7679; outline-offset: 2px; }
.rs-detail-back span { color: #4A5353; }
.rs-detail-back:hover span { color: #0F7679; }
`;

const PANEL: React.CSSProperties = {
  border: `1px solid ${BORDER_SOFT}`,
  borderRadius: '16px',
  backgroundColor: '#FFFFFF',
  minWidth: 0,
};

const H2: React.CSSProperties = { margin: 0, fontSize: '16px', fontWeight: 600, color: INK, lineHeight: '24px' };
const NUM: React.CSSProperties = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

const paidAt = (p: SalePayment) => toDate(p.paymentDate)?.getTime() ?? toDate(p.createdAt)?.getTime() ?? 0;

const RecentSalesDetail = () => {
  const [sale, setSale] = useState<any>(null);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [language, setLanguage] = useState<any>(null);
  const [cardPaymentId, setCardPaymentId] = useState<string>('');
  const path = window.location.pathname;
  const { isMobile } = useResponsive();
  const id: string = path.split('/').pop() || '';
  const preLanguage = localStorage.getItem('language');
  const tr = (key: string) => t(language, key, key);

  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  const fetchData = async (saleId: string) => {
    webLoadingTrue();
    try {
      const response: any = await getDetailTansactions(saleId);
      setSale(response.data.data);
      setLoadState('ready');
    } catch (error) {
      console.error('Error loading sale:', error);
      // A failed refresh keeps the sale already on screen.
      setLoadState(current => (current === 'ready' ? current : 'error'));
    } finally {
      webLoadingFalse();
    }
  };

  useEffect(() => {
    fetchData(id);
    const handleNewToken = () => {
      fetchData(id);
    };
    document.addEventListener('newToken', handleNewToken);
    return () => {
      document.removeEventListener('newToken', handleNewToken);
    };
  }, [path]);

  const goBack = () => webNavigate('/' + preLanguage + '/recentSales');

  const backButton = (
    <button
      type="button"
      className="rs-detail-back"
      onClick={goBack}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        alignSelf: 'flex-start',
        height: '32px',
        padding: '0 8px 0 2px',
        border: 'none',
        borderRadius: '8px',
        background: 'none',
        cursor: 'pointer',
      }}
    >
      <BackIcon />
      <span style={{ fontSize: '14px', fontWeight: 500 }}>{tr('Back')}</span>
    </button>
  );

  const tenderNameOf = (p: SalePayment) => {
    const type = p.deviceTender?.tenderType;
    const typeLabel = type ? TENDER_TYPE_LABELS[type] : '';
    return (p.deviceTender?.tenderName ?? '').trim() || (typeLabel ? tr(typeLabel) : tr('Payment'));
  };

  const root = (children: React.ReactNode) => (
    <div className="h-full font-inter" style={{ height: '100%', overflowY: 'auto', fontFamily: 'Inter, sans-serif', color: TEXT }}>
      <style>{PAGE_CSS}</style>
      <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '16px' : '20px', paddingBottom: '24px', minWidth: 0 }}>
        {children}
      </div>
    </div>
  );

  if (!sale) {
    return root(
      <>
        {backButton}
        {loadState === 'error' ? (
          <div style={{ ...PANEL, padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '12px' }}>
            <p style={{ margin: 0, fontSize: '15px', fontWeight: 500, color: TEXT }}>{tr('This sale could not be loaded.')}</p>
            <button
              type="button"
              className="rs-detail-btn"
              onClick={() => fetchData(id)}
              style={{ height: '36px', padding: '0 16px', borderRadius: '999px', border: `1px solid ${BORDER_STRONG}`, color: TEAL, fontSize: '14px', fontWeight: 500, cursor: 'pointer' }}
            >
              {tr('Try again')}
            </button>
          </div>
        ) : (
          <div aria-busy="true" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[44, 96, 220].map(h => (
              <div key={h} style={{ height: `${h}px`, borderRadius: '16px', backgroundColor: SURFACE_SOFT, border: `1px solid ${BORDER_SOFT}` }} />
            ))}
          </div>
        )}
      </>
    );
  }

  /* ---------------- Header ---------------- */

  const txnType = Number(sale.txnType ?? 1);
  const typePill = TYPE_PILLS[txnType];
  const refunded = txnType === 1 && ((sale.transactionRefunds?.length ?? 0) > 0 || !!sale.refundTxnId);
  const when = toDate(sale.txnDate);
  const userName = `${sale.user?.firstName ?? ''} ${sale.user?.lastName ?? ''}`.trim();
  const tableName = (sale.tableName || sale.floorPlanDetail?.name || '').trim();
  const facts = [
    { label: 'Location', value: sale.location?.name ?? '' },
    { label: 'User', value: userName },
    { label: 'Table', value: tableName },
    { label: 'Device serial', value: sale.device?.serialNo ?? '', mono: true },
  ].filter(f => f.value);

  const header = (
    <header style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: isMobile ? '22px' : '26px', fontWeight: 600, lineHeight: 1.25, color: INK, overflowWrap: 'anywhere' }}>
              {sale.invoiceNo || '—'}
            </h1>
            {typePill && <Pill label={tr(typePill.label)} kind={typePill.kind} />}
            {sale.voided && txnType !== 3 && <Pill label={tr('Voided')} kind="off" />}
            {refunded && <Pill label={tr('Refunded')} kind="danger" />}
          </div>
          {when && (
            <p style={{ margin: '6px 0 0', fontSize: '14px', color: TEXT_MUTED, ...NUM }}>
              {formatDay(when)} · {formatClock(when)}
            </p>
          )}
        </div>
        {/* No refund action is wired yet, so it stays disabled; shown only while the API says the sale can be refunded. */}
        {sale.canRefund && (
          <button
            type="button"
            className="rs-detail-btn"
            disabled
            style={{
              height: '40px',
              padding: '0 20px',
              borderRadius: '999px',
              border: `1px solid ${TEAL}`,
              color: TEAL,
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'not-allowed',
              opacity: 0.55,
              flex: 'none',
            }}
          >
            {tr('Refund')}
          </button>
        )}
      </div>
      {facts.length > 0 && (
        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: '12px 24px',
            margin: 0,
            padding: '14px 16px',
            borderRadius: '12px',
            backgroundColor: SURFACE_SOFT,
            border: `1px solid ${BORDER_SOFT}`,
          }}
        >
          {facts.map(f => (
            <div key={f.label} style={{ minWidth: 0 }}>
              <dt style={{ fontSize: '12.5px', color: TEXT_DIM, lineHeight: '18px' }}>{tr(f.label)}</dt>
              <dd
                style={{
                  margin: '2px 0 0',
                  fontSize: f.mono ? '13px' : '14px',
                  fontFamily: f.mono ? MONO : undefined,
                  fontWeight: 500,
                  color: TEXT,
                  lineHeight: '20px',
                  overflowWrap: 'anywhere',
                }}
              >
                {f.value}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </header>
  );

  /* ---------------- Totals ---------------- */

  const totalRows = [
    { label: 'Net', value: sale.net },
    { label: 'Tax', value: sale.tax },
    { label: 'Discount', value: sale.discount },
    { label: 'Service charge', value: sale.serviceCharge },
    { label: 'Tip', value: sale.tip },
  ];

  const totals = (
    <section aria-labelledby="rs-detail-total" style={{ ...PANEL, padding: isMobile ? '16px' : '20px' }}>
      <h2 id="rs-detail-total" style={{ margin: 0, fontSize: '13px', fontWeight: 500, color: TEXT_DIM }}>
        {tr('Total amount')}
      </h2>
      <p style={{ margin: '4px 0 12px', fontSize: isMobile ? '28px' : '30px', fontWeight: 600, color: INK, lineHeight: 1.2, ...NUM }}>
        {money(sale.amount)}
      </p>
      <dl style={{ margin: 0 }}>
        {totalRows.map(row => (
          <div
            key={row.label}
            style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '9px 0', borderTop: `1px solid ${BORDER_SOFT}` }}
          >
            <dt style={{ fontSize: '14px', color: TEXT_MUTED }}>{tr(row.label)}</dt>
            <dd style={{ margin: 0, fontSize: '14px', fontWeight: 500, color: toNumber(row.value) === 0 ? TEXT_FAINT : TEXT, ...NUM }}>
              {money(row.value)}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );

  /* ---------------- Payments ---------------- */

  const payments: SalePayment[] = [...(sale.payments ?? [])].sort((a, b) => paidAt(a) - paidAt(b));
  const activePayments = payments.filter(p => !p.isVoided);
  const totalPaid = activePayments.reduce((sum, p) => sum + toNumber(p.paymentAmount), 0);
  const cardPayment = payments.find(p => p.id === cardPaymentId) ?? null;

  const paymentRow = (p: SalePayment, index: number) => {
    const name = tenderNameOf(p);
    const type = p.deviceTender?.tenderType;
    const typeLabel = type && TENDER_TYPE_LABELS[type] ? tr(TENDER_TYPE_LABELS[type]) : '';
    const attempts = p.cardPayments ?? [];
    const attempt = primaryAttempt(attempts);
    // The card swatch already says "card", so the type label shows only without one.
    const showType = !attempt && !!typeLabel && typeLabel.toLowerCase() !== name.toLowerCase();
    const brand = attempt ? cardBrandOf(attempt.cardType) : null;
    return (
      <li key={p.id ?? index} style={{ display: 'flex', gap: '12px', padding: '12px 0', borderTop: index > 0 ? `1px solid ${BORDER_SOFT}` : 'none' }}>
        <TenderAvatar type={type} name={name} />
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px', justifyContent: 'center', minHeight: '36px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', minWidth: 0, fontSize: '14px', fontWeight: 500, color: TEXT }}>
              <span style={{ overflowWrap: 'anywhere' }}>{name}</span>
              {p.isVoided && <Pill label={tr('Voided')} kind="off" />}
            </span>
            <span
              style={{
                fontSize: '15px',
                fontWeight: 600,
                color: p.isVoided ? TEXT_FAINT : INK,
                textDecoration: p.isVoided ? 'line-through' : undefined,
                ...NUM,
              }}
            >
              {money(p.paymentAmount)}
            </span>
          </div>
          {(showType || attempt) && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px 12px', flexWrap: 'wrap' }}>
              {/* On mobile the button always gets its own line, so a list of cards keeps one edge. */}
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px 8px',
                  flexWrap: 'wrap',
                  minWidth: 0,
                  flexBasis: isMobile ? '100%' : undefined,
                  fontSize: '13px',
                  color: TEXT_DIM,
                }}
              >
                {showType && <span>{typeLabel}</span>}
                {attempt && brand && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                    <MiniCard brand={brand} />
                    <span style={{ color: TEXT_MUTED, fontWeight: 500 }}>{brand.name || tr('Card')}</span>
                    {attempt.lastFour && <span style={{ fontFamily: MONO, fontSize: '12.5px', color: TEXT_MUTED }}>•••• {attempt.lastFour}</span>}
                  </span>
                )}
                {attempts.length > 1 && <Pill label={`${attempts.length} ${tr('attempts')}`} kind="off" />}
              </span>
              {attempt && (
                <button
                  type="button"
                  className="rs-detail-btn"
                  aria-haspopup="dialog"
                  aria-label={[`${tr('View card')}:`, brand?.name || tr('Card'), endingIn(attempt.lastFour, language)].filter(Boolean).join(' ')}
                  onClick={e => {
                    // Safari does not focus a clicked button; the dialog returns focus to it on close.
                    e.currentTarget.focus();
                    setCardPaymentId(p.id);
                  }}
                  style={{
                    height: '32px',
                    padding: '0 12px',
                    borderRadius: '999px',
                    border: `1px solid ${BORDER_STRONG}`,
                    color: TEAL,
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    flex: 'none',
                  }}
                >
                  {tr('View card')}
                </button>
              )}
            </div>
          )}
        </div>
      </li>
    );
  };

  const paymentsPanel = (
    <section aria-labelledby="rs-detail-payments" style={{ ...PANEL, padding: isMobile ? '16px' : '20px', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
        <h2 id="rs-detail-payments" style={H2}>
          {tr('Payments')}
        </h2>
        {activePayments.length > 1 && <Pill label={tr('Split payment')} kind="teal" />}
      </div>
      {payments.length === 0 ? (
        <p style={{ margin: '8px 0 0', fontSize: '14px', color: TEXT_DIM }}>{tr('No payments recorded')}</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>{payments.map(paymentRow)}</ul>
      )}
      {activePayments.length > 1 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '12px',
            marginTop: 'auto',
            paddingTop: '12px',
            borderTop: `1px solid ${BORDER_SOFT}`,
            fontSize: '14px',
            fontWeight: 600,
            color: INK,
          }}
        >
          <span>{tr('Total paid')}</span>
          <span style={NUM}>{money(totalPaid)}</span>
        </div>
      )}
    </section>
  );

  /* ---------------- Items ---------------- */

  const items: any[] = (sale.transactionDetails ?? []).filter((v: any) => !v.isVoided);
  const refundedTag = (item: any) =>
    toNumber(item?.refundQuantity) > 0 ? (
      <span style={{ display: 'inline-block', marginTop: '6px' }}>
        <Pill label={`${tr('Refunded')}: ${quantity(item.refundQuantity)}`} kind="danger" />
      </span>
    ) : null;
  const dimIfZero = (value: unknown): React.CSSProperties => ({ color: toNumber(value) === 0 ? TEXT_FAINT : TEXT });

  const TH: React.CSSProperties = {
    padding: '11px 16px',
    fontSize: '12.5px',
    fontWeight: 600,
    letterSpacing: '0.02em',
    color: TEXT_MUTED,
    whiteSpace: 'nowrap',
    textAlign: 'right',
    borderBottom: `1px solid ${BORDER_SOFT}`,
  };
  const TD: React.CSSProperties = { padding: '14px 16px', fontSize: '14px', textAlign: 'right', verticalAlign: 'top', borderTop: `1px solid ${BORDER_SOFT}`, ...NUM };

  const itemsTable = (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: '#FAFBFB' }}>
            <th scope="col" style={{ ...TH, textAlign: 'left', width: '100%' }}>
              {tr('Product name')}
            </th>
            {['QTY', 'Price', 'Discount', 'Net', 'Tax', 'Amount'].map(key => (
              <th key={key} scope="col" style={TH}>
                {tr(key)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <tr key={item.id ?? index}>
              <td style={{ ...TD, textAlign: 'left', whiteSpace: 'normal', minWidth: '180px', borderTop: index === 0 ? 'none' : TD.borderTop }}>
                <div style={{ fontWeight: 500, color: TEXT, overflowWrap: 'anywhere' }}>{item.productName ?? ''}</div>
                {item.productSku && (
                  <div style={{ marginTop: '2px', fontFamily: MONO, fontSize: '12px', color: TEXT_FAINT, overflowWrap: 'anywhere' }}>{item.productSku}</div>
                )}
                {refundedTag(item)}
              </td>
              {[
                { key: 'qty', node: quantity(item.quantity), style: { color: TEXT } },
                { key: 'price', node: money(item.price), style: { color: TEXT } },
                { key: 'discount', node: money(item.discount), style: dimIfZero(item.discount) },
                { key: 'net', node: money(item.net), style: { color: TEXT } },
                { key: 'tax', node: money(item.tax), style: dimIfZero(item.tax) },
                { key: 'amount', node: money(item.amount), style: { color: INK, fontWeight: 600 } },
              ].map(cell => (
                <td key={cell.key} style={{ ...TD, ...cell.style, borderTop: index === 0 ? 'none' : TD.borderTop }}>
                  {cell.node}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const itemCards = (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {items.map((item, index) => (
        <li key={item.id ?? index} style={{ padding: '14px 16px', borderTop: index === 0 ? 'none' : `1px solid ${BORDER_SOFT}` }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: '15px', fontWeight: 500, color: TEXT, lineHeight: '21px', overflowWrap: 'anywhere' }}>{item.productName ?? ''}</div>
              <div style={{ marginTop: '3px', fontSize: '13px', color: TEXT_DIM, overflowWrap: 'anywhere' }}>
                {item.productSku && <span style={{ fontFamily: MONO, fontSize: '12px' }}>{item.productSku} · </span>}
                <span style={NUM}>
                  {quantity(item.quantity)} × {money(item.price)}
                </span>
              </div>
            </div>
            <span style={{ fontSize: '15px', fontWeight: 600, color: INK, ...NUM }}>{money(item.amount)}</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginTop: '8px', fontSize: '12.5px', color: TEXT_DIM }}>
            {[
              { label: 'Net', value: item.net },
              { label: 'Tax', value: item.tax },
              ...(toNumber(item.discount) !== 0 ? [{ label: 'Discount', value: item.discount }] : []),
            ].map(part => (
              <span key={part.label}>
                {tr(part.label)} <span style={{ color: TEXT_MUTED, fontWeight: 500, ...NUM }}>{money(part.value)}</span>
              </span>
            ))}
          </div>
          {refundedTag(item)}
        </li>
      ))}
    </ul>
  );

  const itemsPanel = (
    <section aria-labelledby="rs-detail-items" style={{ ...PANEL, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: isMobile ? '14px 16px' : '16px 20px', borderBottom: `1px solid ${BORDER_SOFT}` }}>
        <h2 id="rs-detail-items" style={H2}>
          {tr('Items')}
        </h2>
        <span style={{ fontSize: '13px', color: TEXT_DIM, ...NUM }}>{items.length}</span>
      </div>
      {items.length === 0 ? (
        <p style={{ margin: 0, padding: '16px 20px', fontSize: '14px', color: TEXT_DIM }}>—</p>
      ) : isMobile ? (
        itemCards
      ) : (
        itemsTable
      )}
    </section>
  );

  return root(
    <>
      {backButton}
      {header}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))', gap: '16px' }}>
        {totals}
        {paymentsPanel}
      </div>
      {itemsPanel}
      <CardPaymentDialog
        open={!!cardPayment}
        onClose={() => setCardPaymentId('')}
        language={language}
        payment={cardPayment}
        tenderName={cardPayment ? tenderNameOf(cardPayment) : ''}
        isMobile={isMobile}
      />
    </>
  );
};
export default RecentSalesDetail;
